import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { FertilizationDialogFields, NContributionFields } from '../main';
import { IIrrigationMethod } from '../../../models/irrigation-method/interfaces';
import { CalculateService } from '../../../services/calculate.service';
import { BOKA } from '../../../classes/number';
import { eIrrigationUnit } from '../../../interfaces/constants';
import { FertilizationEvent } from '../fertilizationEvent';
import { IFertilizationEvent, nUnits } from '../interfaces';
import { takeUntil } from 'rxjs/operators';
import { NContributionUpdateService } from './n-contribution-update-service';

@Component({
	moduleId: module.id,
	selector: 'n-contribution',
	templateUrl: 'n-contribution.html',
	styleUrls: []
})

export class NContributionComponent implements OnInit, OnDestroy {
	protected _subscriptions$: Subject<boolean>;

	@Input()
	public form: FormGroup;

	@Input()
	public nContributionForm: FormGroup;

	@Input()
	public irrigationMethods: IIrrigationMethod[];

	@Input()
	public NContribution: {
		UIVisible: boolean,
		calculatedPPM: number,
		recommendationHours: number,
		nextFertilizationDate: Date,
		canSaveWellsAsPlantingDefault?: boolean,
	}

	@Input()
	public units: nUnits;

	public nUnits = nUnits;
	public totalWellPercentage: number;
	public irrigationUnit: eIrrigationUnit;
	public irrigationUnits = eIrrigationUnit;

	public get f(): { [key in FertilizationDialogFields]: AbstractControl } {
		if (!this.form) {
			return null
		};

		return this.form.controls as { [key in FertilizationDialogFields]: AbstractControl };
	}

	public get fn(): { [key in NContributionFields]: AbstractControl } {
		if (!this.nContributionForm) {
			return null;
		}

		return this.nContributionForm.controls as { [key in NContributionFields]: AbstractControl };
	}

	constructor(
		private calculateService: CalculateService,
		private nContributionUpdateService: NContributionUpdateService
	) {
		this.irrigationUnit = eIrrigationUnit.HOURS;
	}

	ngOnInit(): void {
		this._subscriptions$ = new Subject();

		if (!this.fn.wells || (this.fn.wells as FormArray).controls.length === 0) {
			// timing depends on whether this component is spawned by the planner or embedded in the fertilization event dialog
			this.nContributionUpdateService.loaded$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
				this._setupWells();
			});
		} else {
			this._setupWells();
		}
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public calculateNContribution(updateNApplied = false): void {
		let ppm: number;
		let nApplied: number;

		if (this.form.invalid) {
			return;
		}

		if (this.fn.UseWells.value) {
			this.NContribution.calculatedPPM = this.calculateService.calculateAveragePPM(this.fn.wells.value);
			ppm = this.NContribution.calculatedPPM;
		} else {
			ppm = this.fn.PPMTotal.valid ? this.fn.PPMTotal.value : 0;
		}

		if (updateNApplied) {
			nApplied = this.calculateService.calculateNApplied(this.fn.IrrigationAmount.value, ppm);

			this.f.NAppliedFromWater.setValue(
				BOKA.NumberUtil.roundToDecimalPlaces(nApplied, 2)
			); // update NApplied

			// this._updateRecommendation(); // delayed
		}

		// possibly ping for recommendation again here.. but that may be too intensive?
		// (which is where having this on a separate screen would help)
	}

	public setIrrigationUnit(unit: eIrrigationUnit): void {
		this.irrigationUnit = unit;
	}

	public getNAppliedFromWaterInFertilizerUnits(): number {
		if (!this.f) {
			return null;
		}

		return BOKA.NumberUtil.roundToDecimalPlaces(FertilizationEvent.convertNToFertilizerAmount(
			this.f.NAppliedFromWater.value, this.form.value as IFertilizationEvent), 2);
	}

	private _calculateTotalWellPercentage(): void {
		let result = 0;

		for (let well of ((this.fn.wells as FormArray).controls as FormGroup[])) {
			result += well.get('Percentage').value;
		}

		this.totalWellPercentage = result;
	}

	private _setupWells(): void {
		for (let well of (this.fn.wells as FormArray).controls) {
			well.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
				this._calculateTotalWellPercentage();
			});
		}

		this._calculateTotalWellPercentage();
	}
}
